import { useState } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getNextAssessment, startAttempt } from '../api/endpoints'
import { ApiError } from '../api/client'
import { ErrorNotice } from '../components'
import { minutesLabel } from '../lib/time'

/** Instructions, duration and question count, confirmed before the clock starts (FR-C10). Also the transition between tests. */
export default function InstructionsPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const justFinished = (useLocation().state as { submitted?: boolean } | null)?.submitted === true
  const [confirmed, setConfirmed] = useState(false)
  const next = useQuery({ queryKey: ['next'], queryFn: getNextAssessment, staleTime: 0 })
  const start = useMutation({
    mutationFn: startAttempt,
    onSuccess: async (attempt) => {
      await queryClient.invalidateQueries({ queryKey: ['application'] })
      navigate(`/test/${attempt.id}`)
    },
  })

  if (next.isPending) return <p role="status">Loading…</p>
  if (next.isError) return <ErrorNotice error={next.error} />
  const a = next.data
  if (!a) return <Navigate to="/finished" replace />

  const resuming = a.inProgressAttemptId !== null
  const blocked = !a.deviceAllowed
  const code = start.error instanceof ApiError ? start.error.code : ''

  return (
    <div>
      {justFinished && (
        <div className="notice ok" role="status">
          <strong>Your answers are saved and submitted.</strong> The next test is ready when you are.
        </div>
      )}
      <h1>{resuming ? `Continue: ${a.name}` : a.name}</h1>
      <div className="card stack">
        <ul>
          <li><strong>{a.questionCount}</strong> questions</li>
          <li><strong>{minutesLabel(a.durationSeconds)}</strong> in total{resuming ? ' (the clock has been running since you started)' : ''}</li>
          {a.sections.length > 1 && <li>Sections: {a.sections.map((s) => `${s.name} (${s.questions})`).join(', ')}</li>}
        </ul>
        {a.instructions.length > 0 && (
          <>
            <h2>How this test works</h2>
            <ul>{a.instructions.map((line, i) => <li key={i}>{line}</li>)}</ul>
          </>
        )}
        <h2>Before you begin</h2>
        <ul>
          <li>The timer runs on our server. Closing the page or losing connection does not stop it, but you can sign in again and carry on where you left off.</li>
          <li>Each answer saves as soon as you choose it. The indicator at the top shows <em>Saved</em>, <em>Saving</em> or <em>Offline</em>.</li>
          <li>You can move between questions, mark ones to review, and clear an answer.</li>
          <li>When the time ends, your answers are submitted automatically.</li>
        </ul>
        {blocked && (
          <div role="alert" className="notice error">
            This test must be taken on a laptop or desktop computer. Please switch device. If you have no access to one, contact admissions.
          </div>
        )}
        {!blocked && !resuming && (
          <label className="check">
            <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
            <span>I have read the instructions and I am ready to start.</span>
          </label>
        )}
        {code === 'DEVICE_NOT_ALLOWED' ? null : <ErrorNotice error={start.error} />}
        <button className="primary" disabled={blocked || (!resuming && !confirmed) || start.isPending} onClick={() => start.mutate()}>
          {start.isPending ? 'Starting…' : resuming ? 'Continue the test' : 'Start the test'}
        </button>
      </div>
    </div>
  )
}
